import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD'
}).format(amount || 0)

const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', {
  month: 'long', day: 'numeric', year: 'numeric'
})

const formatDateShort = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric'
})

// ─── Shared Header ───────────────────────────────────────────────────────────
function addHeader(doc, trust, reportTitle, dateFrom, dateTo) {
  const pageWidth = doc.internal.pageSize.getWidth()

  // App name
  doc.setFontSize(10)
  doc.setTextColor(100, 100, 100)
  doc.text('TrustLedger', 14, 14)

  // Report Title
  doc.setFontSize(18)
  doc.setTextColor(30, 30, 30)
  doc.text(reportTitle, 14, 28)

  // Trust name
  doc.setFontSize(12)
  doc.setTextColor(60, 60, 60)
  doc.text(trust.name, 14, 38)

  // Date range
  doc.setFontSize(10)
  doc.setTextColor(120, 120, 120)
  doc.text(
    `For the period: ${formatDate(dateFrom)} through ${formatDate(dateTo)}`,
    14, 46
  )

  // State of domicile
  if (trust.state_of_domicile) {
    doc.text(`State of Domicile: ${trust.state_of_domicile}`, 14, 53)
  }

  // Generated date (right aligned)
  doc.text(
    `Generated: ${formatDate(new Date().toISOString())}`,
    pageWidth - 14, 14,
    { align: 'right' }
  )

  // Divider line
  doc.setDrawColor(200, 200, 200)
  doc.line(14, 58, pageWidth - 14, 58)

  return 65 // return Y position after header
}

// ─── Footer ──────────────────────────────────────────────────────────────────
function addFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    doc.text(
      'This report was generated by TrustLedger. It is intended for informational purposes only.',
      14, pageHeight - 10
    )
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - 14, pageHeight - 10,
      { align: 'right' }
    )
  }
}

// ─── Annual Accounting Report ─────────────────────────────────────────────────
function generateAnnualAccounting(doc, { trust, transactions, accounts, beneficiaries, dateFrom, dateTo }) {
  let y = addHeader(doc, trust, 'Annual Accounting', dateFrom, dateTo)

  // ── Account Summary ──
  doc.setFontSize(12)
  doc.setTextColor(30, 30, 30)
  doc.text('Account Summary', 14, y)
  y += 6

  autoTable(doc, {
    startY: y,
    head: [['Account', 'Type', 'Principal Balance', 'Income Balance', 'Total Balance']],
    body: accounts.map(a => [
      a.name,
      a.account_type?.replace('_', ' '),
      formatCurrency(a.principal_balance),
      formatCurrency(a.income_balance),
      formatCurrency(a.current_balance)
    ]),
    foot: [[
      'TOTAL', '',
      formatCurrency(accounts.reduce((s, a) => s + (a.principal_balance || 0), 0)),
      formatCurrency(accounts.reduce((s, a) => s + (a.income_balance || 0), 0)),
      formatCurrency(accounts.reduce((s, a) => s + (a.current_balance || 0), 0))
    ]],
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 9 },
    footStyles: { fillColor: [243, 244, 246], textColor: [30, 30, 30], fontStyle: 'bold' },
    bodyStyles: { fontSize: 9 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    margin: { left: 14, right: 14 }
  })

  y = doc.lastAutoTable.finalY + 12

  // ── Income Summary ──
  const incomeTransactions = transactions.filter(t => t.type === 'income')
  const totalPrincipalIn = incomeTransactions.reduce((s, t) => s + (t.principal_amount || 0), 0)
  const totalIncomeIn = incomeTransactions.reduce((s, t) => s + (t.income_amount || 0), 0)

  doc.setFontSize(12)
  doc.setTextColor(30, 30, 30)
  doc.text('Income Received', 14, y)
  y += 6

  autoTable(doc, {
    startY: y,
    head: [['Date', 'Description', 'Account', 'Category', 'Principal', 'Income', 'Total']],
    body: incomeTransactions.map(t => [
      formatDateShort(t.date),
      t.description,
      t.accounts?.name || '—',
      t.categories?.name || '—',
      formatCurrency(t.principal_amount),
      formatCurrency(t.income_amount),
      formatCurrency(t.amount)
    ]),
    foot: [[
      '', 'TOTAL INCOME', '', '',
      formatCurrency(totalPrincipalIn),
      formatCurrency(totalIncomeIn),
      formatCurrency(incomeTransactions.reduce((s, t) => s + (t.amount || 0), 0))
    ]],
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 8 },
    footStyles: { fillColor: [243, 244, 246], textColor: [30, 30, 30], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    margin: { left: 14, right: 14 }
  })

  y = doc.lastAutoTable.finalY + 12

  // ── Disbursements & Expenses ──
  const expenseTransactions = transactions.filter(t =>
    ['disbursement', 'fee', 'tax'].includes(t.type)
  )
  const totalPrincipalOut = expenseTransactions.reduce((s, t) => s + (t.principal_amount || 0), 0)
  const totalIncomeOut = expenseTransactions.reduce((s, t) => s + (t.income_amount || 0), 0)

  doc.setFontSize(12)
  doc.setTextColor(30, 30, 30)
  doc.text('Disbursements & Expenses', 14, y)
  y += 6

  autoTable(doc, {
    startY: y,
    head: [['Date', 'Description', 'Type', 'Beneficiary', 'Principal', 'Income', 'Total']],
    body: expenseTransactions.map(t => [
      formatDateShort(t.date),
      t.description,
      t.type,
      t.beneficiaries?.full_name || '—',
      formatCurrency(t.principal_amount),
      formatCurrency(t.income_amount),
      formatCurrency(t.amount)
    ]),
    foot: [[
      '', 'TOTAL DISBURSEMENTS', '', '',
      formatCurrency(totalPrincipalOut),
      formatCurrency(totalIncomeOut),
      formatCurrency(expenseTransactions.reduce((s, t) => s + (t.amount || 0), 0))
    ]],
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 8 },
    footStyles: { fillColor: [243, 244, 246], textColor: [30, 30, 30], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    margin: { left: 14, right: 14 }
  })

  y = doc.lastAutoTable.finalY + 12

  // ── Net Summary ──
  const totalIn = incomeTransactions.reduce((s, t) => s + (t.amount || 0), 0)
  const totalOut = expenseTransactions.reduce((s, t) => s + (t.amount || 0), 0)

  autoTable(doc, {
    startY: y,
    body: [
      ['Total Income', formatCurrency(totalPrincipalIn), formatCurrency(totalIncomeIn), formatCurrency(totalIn)],
      ['Total Disbursements & Expenses', formatCurrency(totalPrincipalOut), formatCurrency(totalIncomeOut), formatCurrency(totalOut)],
      ['Net Change', formatCurrency(totalPrincipalIn - totalPrincipalOut), formatCurrency(totalIncomeIn - totalIncomeOut), formatCurrency(totalIn - totalOut)],
    ],
    head: [['', 'Principal', 'Income', 'Total']],
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 9, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [239, 246, 255] },
    margin: { left: 14, right: 14 }
  })

  // ── Beneficiaries ──
  if (beneficiaries.length > 0) {
    y = doc.lastAutoTable.finalY + 12
    doc.setFontSize(12)
    doc.setTextColor(30, 30, 30)
    doc.text('Beneficiaries', 14, y)
    y += 6

    autoTable(doc, {
      startY: y,
      head: [['Name', 'Type', 'Interest', 'Status']],
      body: beneficiaries.map(b => [
        b.full_name,
        b.beneficiary_type?.replace('_', ' '),
        b.interest_description || '—',
        b.is_active ? 'Active' : 'Inactive'
      ]),
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      margin: { left: 14, right: 14 }
    })
  }
}

// ─── Disbursement History Report ──────────────────────────────────────────────
function generateDisbursementHistory(doc, { trust, transactions, beneficiaries, dateFrom, dateTo }) {
  let y = addHeader(doc, trust, 'Disbursement History by Beneficiary', dateFrom, dateTo)

  const disbursements = transactions.filter(t => t.type === 'disbursement')

  if (disbursements.length === 0) {
    doc.setFontSize(10)
    doc.setTextColor(120, 120, 120)
    doc.text('No disbursements found for this period.', 14, y)
    return
  }

  // Group by beneficiary
  const grouped = {}
  disbursements.forEach(t => {
    const name = t.beneficiaries?.full_name || 'Unassigned'
    if (!grouped[name]) grouped[name] = []
    grouped[name].push(t)
  })

  Object.entries(grouped).forEach(([beneficiaryName, txs]) => {
    doc.setFontSize(11)
    doc.setTextColor(30, 30, 30)
    doc.text(beneficiaryName, 14, y)
    y += 5

    autoTable(doc, {
      startY: y,
      head: [['Date', 'Description', 'Principal', 'Income', 'Total']],
      body: txs.map(t => [
        formatDateShort(t.date),
        t.description,
        formatCurrency(t.principal_amount),
        formatCurrency(t.income_amount),
        formatCurrency(t.amount)
      ]),
      foot: [[
        '', `Total for ${beneficiaryName}`,
        formatCurrency(txs.reduce((s, t) => s + (t.principal_amount || 0), 0)),
        formatCurrency(txs.reduce((s, t) => s + (t.income_amount || 0), 0)),
        formatCurrency(txs.reduce((s, t) => s + (t.amount || 0), 0))
      ]],
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 9 },
      footStyles: { fillColor: [243, 244, 246], fontStyle: 'bold' },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      margin: { left: 14, right: 14 }
    })

    y = doc.lastAutoTable.finalY + 10
  })

  // Grand total
  autoTable(doc, {
    startY: y,
    body: [[
      'GRAND TOTAL',
      formatCurrency(disbursements.reduce((s, t) => s + (t.principal_amount || 0), 0)),
      formatCurrency(disbursements.reduce((s, t) => s + (t.income_amount || 0), 0)),
      formatCurrency(disbursements.reduce((s, t) => s + (t.amount || 0), 0))
    ]],
    head: [['', 'Principal', 'Income', 'Total']],
    headStyles: { fillColor: [30, 64, 175], textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 10, fontStyle: 'bold', fillColor: [239, 246, 255] },
    margin: { left: 14, right: 14 }
  })
}

// ─── Transaction Ledger Report ────────────────────────────────────────────────
function generateTransactionLedger(doc, { trust, transactions, dateFrom, dateTo }) {
  addHeader(doc, trust, 'Transaction Ledger', dateFrom, dateTo)

  autoTable(doc, {
    startY: 65,
    head: [['Date', 'Description', 'Type', 'Account', 'Principal', 'Income', 'Total', 'Rec.']],
    body: transactions.map(t => [
      formatDateShort(t.date),
      t.description,
      t.type?.replace('_', ' '),
      t.accounts?.name || '—',
      formatCurrency(t.principal_amount),
      formatCurrency(t.income_amount),
      (t.type === 'income' ? '+' : '-') + formatCurrency(t.amount),
      t.reconciled ? '✓' : ''
    ]),
    foot: [[
      '', 'TOTALS', '', '',
      formatCurrency(transactions.reduce((s, t) => s + (t.principal_amount || 0), 0)),
      formatCurrency(transactions.reduce((s, t) => s + (t.income_amount || 0), 0)),
      '', ''
    ]],
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 8 },
    footStyles: { fillColor: [243, 244, 246], fontStyle: 'bold' },
    bodyStyles: { fontSize: 8 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    margin: { left: 14, right: 14 }
  })
}

// ─── Principal & Income Summary Report ───────────────────────────────────────
function generatePrincipalIncomeSummary(doc, { trust, transactions, accounts, dateFrom, dateTo }) {
  let y = addHeader(doc, trust, 'Principal & Income Summary', dateFrom, dateTo)

  // Principal section
  doc.setFontSize(12)
  doc.setTextColor(30, 30, 30)
  doc.text('Principal Account', 14, y)
  y += 6

  const principalTxs = transactions.filter(t => (t.principal_amount || 0) > 0)

  autoTable(doc, {
    startY: y,
    head: [['Date', 'Description', 'Type', 'Amount']],
    body: principalTxs.map(t => [
      formatDateShort(t.date),
      t.description,
      t.type,
      (t.type === 'income' ? '+' : '-') + formatCurrency(t.principal_amount)
    ]),
    foot: [[
      '', 'NET PRINCIPAL',  '',
      formatCurrency(
        principalTxs.reduce((s, t) =>
          t.type === 'income'
            ? s + (t.principal_amount || 0)
            : s - (t.principal_amount || 0), 0)
      )
    ]],
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 9 },
    footStyles: { fillColor: [243, 244, 246], fontStyle: 'bold' },
    bodyStyles: { fontSize: 9 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    margin: { left: 14, right: 14 }
  })

  y = doc.lastAutoTable.finalY + 12

  // Income section
  doc.setFontSize(12)
  doc.setTextColor(30, 30, 30)
  doc.text('Income Account', 14, y)
  y += 6

  const incomeTxs = transactions.filter(t => (t.income_amount || 0) > 0)

  autoTable(doc, {
    startY: y,
    head: [['Date', 'Description', 'Type', 'Amount']],
    body: incomeTxs.map(t => [
      formatDateShort(t.date),
      t.description,
      t.type,
      (t.type === 'income' ? '+' : '-') + formatCurrency(t.income_amount)
    ]),
    foot: [[
      '', 'NET INCOME', '',
      formatCurrency(
        incomeTxs.reduce((s, t) =>
          t.type === 'income'
            ? s + (t.income_amount || 0)
            : s - (t.income_amount || 0), 0)
      )
    ]],
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 9 },
    footStyles: { fillColor: [243, 244, 246], fontStyle: 'bold' },
    bodyStyles: { fontSize: 9 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    margin: { left: 14, right: 14 }
  })
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export function generateReport({ type, trust, transactions, accounts, beneficiaries, dateFrom, dateTo, generatedBy }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })

  switch (type) {
    case 'annual_accounting':
      generateAnnualAccounting(doc, { trust, transactions, accounts, beneficiaries, dateFrom, dateTo })
      break
    case 'disbursement_history':
      generateDisbursementHistory(doc, { trust, transactions, beneficiaries, dateFrom, dateTo })
      break
    case 'transaction_ledger':
      generateTransactionLedger(doc, { trust, transactions, dateFrom, dateTo })
      break
    case 'principal_income_summary':
      generatePrincipalIncomeSummary(doc, { trust, transactions, accounts, dateFrom, dateTo })
      break
    default:
      generateAnnualAccounting(doc, { trust, transactions, accounts, beneficiaries, dateFrom, dateTo })
  }

  addFooter(doc)

  // Generate filename
  const trustName = trust.name.replace(/\s+/g, '_')
  const reportLabels = {
    annual_accounting: 'Annual_Accounting',
    disbursement_history: 'Disbursement_History',
    transaction_ledger: 'Transaction_Ledger',
    principal_income_summary: 'Principal_Income_Summary'
  }
  const filename = `${trustName}_${reportLabels[type] || 'Report'}_${dateFrom}_to_${dateTo}.pdf`

  doc.save(filename)
}